import { useEffect, useState } from 'react';
import { Mail, ShieldCheck, X } from 'lucide-react';
import { useQueryClient } from '@tanstack/react-query';
import toast from 'react-hot-toast';
import { useAuth } from '../contexts/AuthContext';
import { useUserSettings } from '../hooks/useUserSettings';
import { apiService } from '../services/api';
import ModalPortal from './ModalPortal';

export default function CommunicationEmailPrompt() {
  const { user, refreshUser } = useAuth();
  const { data: settings, isLoading } = useUserSettings();
  const queryClient = useQueryClient();
  const [emailInput, setEmailInput] = useState<string | null>(null);
  const [nameInput, setNameInput] = useState<string | null>(null);
  const [code, setCode] = useState('');
  const [step, setStep] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const handleOpen = () => {
      setOpen(true);
      setStep('email');
      setCode('');
      setError('');
      setEmailInput(null);
      setNameInput(null);
    };
    window.addEventListener('simplysend:change-communication-email', handleOpen);
    return () => window.removeEventListener('simplysend:change-communication-email', handleOpen);
  }, []);

  const email = emailInput ?? (settings?.communicationEmail || user?.email || '');
  const currentName = settings?.settings?.profile?.name?.trim() || '';
  const name = nameInput ?? currentName;
  const needsInitialEmail = Boolean(
    !settings?.communicationEmail ||
    settings?.emailStatus === 'inactive',
  );
  const needsName = needsInitialEmail && !currentName;
  if (!user || isLoading || !settings || (!needsInitialEmail && !open)) return null;

  const requestCode = async () => {
    setError('');
    if (needsName && !name.trim()) {
      setError('Please enter your name.');
      return;
    }
    setBusy(true);
    try {
      if (needsName) {
        await apiService.updateUserSettings({ profile: { name: name.trim() } });
        await queryClient.invalidateQueries({ queryKey: ['userSettings'] });
      }
      await apiService.requestCommunicationEmailVerification(email);
      setStep('code');
      toast.success('Verification code sent');
      await queryClient.invalidateQueries({ queryKey: ['userSettings'] });
    } catch (requestError) {
      setError(requestError instanceof Error ? requestError.message : 'Could not send the verification code.');
    } finally {
      setBusy(false);
    }
  };

  const verifyCode = async () => {
    setError('');
    setBusy(true);
    try {
      await apiService.verifyCommunicationEmail(code);
      await queryClient.invalidateQueries({ queryKey: ['userSettings'] });
      await refreshUser();
      setOpen(false);
      toast.success('Communication email verified');
    } catch (verifyError) {
      setError(verifyError instanceof Error ? verifyError.message : 'Could not verify the code.');
    } finally {
      setBusy(false);
    }
  };

  const closePrompt = () => {
    setOpen(false);
    setStep('email');
    setCode('');
    setError('');
    setEmailInput(null);
    setNameInput(null);
  };

  return (
    <ModalPortal>
      <div className="fixed inset-0 z-100 flex items-center justify-center bg-white p-4 animate-in fade-in duration-200 dark:bg-black">
        <div role="dialog" aria-modal="true" className="w-full max-w-md rounded-3xl border border-zinc-200 bg-white p-6 shadow-2xl animate-in zoom-in-95 duration-200 dark:border-zinc-700 dark:bg-zinc-800">
          <div className="mb-5 flex items-start justify-between gap-3">
            <div className="flex items-start gap-3">
              <div className="rounded-full bg-cyan-100 p-2 text-cyan-700 dark:bg-cyan-950 dark:text-cyan-300"><Mail className="h-5 w-5" /></div>
              <div>
                <h2 className="text-lg font-semibold text-zinc-900 dark:text-zinc-100">{settings.emailStatus === 'inactive' ? 'Verify your communication email' : needsInitialEmail ? 'Add a communication email' : 'Change your communication email'}</h2>
                <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400"><span>{needsInitialEmail ? 'Add and verify an address where Simply Send can send policy and account notifications. You can use your sign-in address if you can receive email there.' : 'Verify a new address where Simply Send can send policy and account notifications.'}</span></p>
              </div>
            </div>
            {open && <button type="button" onClick={closePrompt} disabled={busy} aria-label="Close communication email dialog" className="shrink-0 rounded-full p-1.5 text-zinc-500 hover:bg-zinc-100 hover:text-zinc-700 disabled:cursor-not-allowed disabled:opacity-50 dark:text-zinc-400 dark:hover:bg-zinc-700 dark:hover:text-zinc-200"><X className="h-5 w-5" /></button>}
          </div>
          {step === 'email' ? (
            <>
              {needsName && (
                <>
                  <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300" htmlFor="communication-name">Name</label>
                  <input id="communication-name" type="text" value={name} onChange={(event) => setNameInput(event.target.value)} autoFocus className="mb-4 w-full rounded-full border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100" placeholder="Your name" />
                </>
              )}
              <label className="mb-1 block text-sm font-medium text-zinc-700 dark:text-zinc-300" htmlFor="communication-email">Email address</label>
              <input id="communication-email" type="email" value={email} onChange={(event) => setEmailInput(event.target.value)} autoFocus={!needsName} className="w-full rounded-full border border-zinc-300 bg-white px-3 py-2 text-sm text-zinc-900 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100" placeholder="you@company.com" />
              <button type="button" onClick={() => void requestCode()} disabled={busy || !email.trim() || (needsName && !name.trim())} className="mt-4 w-full rounded-full bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"><span>{busy ? 'Sending…' : 'Send verification code'}</span></button>
            </>
          ) : (
            <>
              <p className="mb-2 text-sm text-zinc-600 dark:text-zinc-400">Enter the six-digit code sent to <strong>{email || settings.communicationEmail || 'your email address'}</strong>.</p>
              <input type="text" inputMode="numeric" maxLength={6} value={code} onChange={(event) => setCode(event.target.value.replace(/\D/g, ''))} autoFocus className="w-full rounded-full border border-zinc-300 bg-white px-3 py-2 text-center text-lg tracking-[0.35em] text-zinc-900 outline-none focus:border-cyan-500 focus:ring-2 focus:ring-cyan-200 dark:border-zinc-600 dark:bg-zinc-800 dark:text-zinc-100" placeholder="000000" />
              <div className="mt-4 flex gap-2">
                <button type="button" onClick={() => setStep('email')} disabled={busy} className="flex-1 rounded-full border border-zinc-300 px-4 py-2 text-sm font-medium text-zinc-700 hover:bg-zinc-50 disabled:opacity-50 dark:border-zinc-600 dark:text-zinc-300 dark:hover:bg-zinc-800"><span>Change email</span></button>
                <button type="button" onClick={() => void verifyCode()} disabled={busy || code.length !== 6} className="flex-1 rounded-full bg-cyan-600 px-4 py-2 text-sm font-medium text-white hover:bg-cyan-700 disabled:cursor-not-allowed disabled:opacity-50"><span>{busy ? 'Verifying…' : 'Verify email'}</span></button>
              </div>
            </>
          )}
          {error && <p role="alert" className="mt-3 text-sm text-red-600 dark:text-red-400">{error}</p>}
          <div className="mt-5 flex items-center gap-2 text-xs text-zinc-500 dark:text-zinc-400"><ShieldCheck className="h-4 w-4" /><span>This address is used only for account communication and can be changed later.</span></div>
        </div>
      </div>
    </ModalPortal>
  );
}
